import React, { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { getTradeNetPnL } from '../../config';
import { getSessionColorDef } from '../../utils/constants';

/**
 * CalendarTab — Monthly calendar view with daily P&L and journaling status.
 * Extracted from TradingJournal.js (Phase 3).
 */
const CalendarTab = ({
  darkMode,
  trades,
  filteredTrades,
  accounts,
  sessions = [],
  selectedAccountFilter,
  activeAccountsOnly,
  activeAccountIds,
  includeResetTradesInStats,
  getAccountById,
  journalEntries,
  economicCalendarSettings,
  onNavigateToJournal,
}) => {

  // Build a map: dateStr -> [{id, name, color, count, pnl}] for sessions on that day
  const sessionsByDate = React.useMemo(() => {
    const map = new Map();
    sessions.forEach(s => {
      // Resolve trades on this session (filteredTrades respects user filter)
      const sTrades = filteredTrades.filter(t => t.sessionId === s.id);
      if (sTrades.length === 0) return;
      // Group by trading date (sessions are normally same-day, but be safe)
      const byDate = new Map();
      sTrades.forEach(t => {
        if (!byDate.has(t.date)) byDate.set(t.date, []);
        byDate.get(t.date).push(t);
      });
      byDate.forEach((tList, dateStr) => {
        const pnl = tList.reduce((sum, t) => {
          const acc = accounts.find(a => a.id === t.accountId);
          const { netPnl } = getTradeNetPnL(t, acc);
          return sum + (isNaN(netPnl) ? 0 : netPnl);
        }, 0);
        if (!map.has(dateStr)) map.set(dateStr, []);
        map.get(dateStr).push({
          id: s.id,
          name: s.name || 'Untitled',
          color: s.color || 'blue',
          count: tList.length,
          pnl
        });
      });
    });
    return map;
  }, [sessions, filteredTrades, accounts]);
  // Local state
  const [currentMonth, setCurrentMonth] = useState(new Date());

  // Calendar helper functions
  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const getTradesForDate = (date) => {
    // Format from local-time fields — toISOString() shifts to UTC and would
    // mismatch trade.date (a local YYYY-MM-DD string) for positive-UTC users.
    const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    return filteredTrades.filter(trade => trade.date === dateStr);
  };

  const getDailyStats = (date) => {
    const dayTrades = getTradesForDate(date);
    if (dayTrades.length === 0) return null;

    // Calculate net P&L for each trade
    const tradesWithNetPnL = dayTrades.map(trade => {
      const tradeAccount = accounts.find(acc => acc.id === trade.accountId);
      const { netPnl } = getTradeNetPnL(trade, tradeAccount);
      return { ...trade, netPnl: isNaN(netPnl) ? 0 : netPnl };
    });

    const totalPnL = tradesWithNetPnL.reduce((sum, trade) => sum + (isNaN(trade.netPnl) ? 0 : trade.netPnl), 0);
    const wins = tradesWithNetPnL.filter(trade => trade.netPnl > 0).length;
    const winRate = (wins / tradesWithNetPnL.length) * 100;
    const dashTradesWithR = tradesWithNetPnL.filter(t => t.rMultiple && parseFloat(t.rMultiple) !== 0);
    const avgR = dashTradesWithR.length > 0 ? dashTradesWithR.reduce((sum, t) => sum + parseFloat(t.rMultiple), 0) / dashTradesWithR.length : 0;

    // Calculate journaling status
    const journaledTrades = tradesWithNetPnL.filter(trade => {
      const hasStopLoss = trade.stopLoss || trade.stopLossPoints;
      if (trade.journaled === undefined) {
        return hasStopLoss;
      }
      return trade.journaled === true && hasStopLoss;
    }).length;

    return {
      trades: tradesWithNetPnL.length,
      pnl: totalPnL,
      winRate: winRate,
      avgR: avgR,
      journaled: journaledTrades,
      allJournaled: journaledTrades === tradesWithNetPnL.length
    };
  };

  const getMonthlyOverview = () => {
    // Compare on YYYY-MM string — trade.date is stored as a local YYYY-MM-DD string,
    // and `new Date("2026-05-01")` parses as UTC midnight, which falls into the previous
    // month for users in negative-UTC timezones and would silently drop 1st-of-month trades.
    const monthPrefix = `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, '0')}`;

    // Apply same filters as filteredTrades (account, active-only, pre-reset) but NOT time period
    let tradesToUse = trades;

    if (selectedAccountFilter !== 'all') {
      tradesToUse = tradesToUse.filter(t => t.accountId === selectedAccountFilter);
    }

    if (activeAccountsOnly && selectedAccountFilter === 'all') {
      tradesToUse = tradesToUse.filter(t => !t.accountId || activeAccountIds.includes(t.accountId));
    }

    if (!includeResetTradesInStats) {
      tradesToUse = tradesToUse.filter(t => {
        if (!t.accountId) return true;
        const account = getAccountById(t.accountId);
        if (!account) return true;
        const currentPeriod = account.currentPeriod || 1;
        return !t.period || t.period === currentPeriod;
      });
    }

    const monthTrades = tradesToUse.filter(trade => trade.date && trade.date.startsWith(monthPrefix));

    if (monthTrades.length === 0) {
      return { totalPnL: 0, totalTrades: 0, winRate: 0, avgR: 0, tradingDays: 0 };
    }

    // Calculate net P&L for each trade
    const tradesWithNetPnL = monthTrades.map(trade => {
      const tradeAccount = accounts.find(acc => acc.id === trade.accountId);
      const { netPnl } = getTradeNetPnL(trade, tradeAccount);
      return { ...trade, netPnl: isNaN(netPnl) ? 0 : netPnl };
    });

    const totalPnL = tradesWithNetPnL.reduce((sum, trade) => sum + (isNaN(trade.netPnl) ? 0 : trade.netPnl), 0);
    const wins = tradesWithNetPnL.filter(trade => trade.netPnl > 0).length;
    const winRate = (wins / tradesWithNetPnL.length) * 100;
    const periodTradesWithR = tradesWithNetPnL.filter(t => t.rMultiple && parseFloat(t.rMultiple) !== 0);
    const avgR = periodTradesWithR.length > 0 ? periodTradesWithR.reduce((sum, t) => sum + parseFloat(t.rMultiple), 0) / periodTradesWithR.length : 0;

    const uniqueDays = new Set(tradesWithNetPnL.map(trade => trade.date)).size;

    return {
      totalPnL,
      totalTrades: tradesWithNetPnL.length,
      winRate,
      avgR,
      tradingDays: uniqueDays
    };
  };

  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(currentMonth);
    const firstDay = getFirstDayOfMonth(currentMonth);
    const today = new Date();

    // Build calendar data organized by weeks
    const weeks = [];
    let currentWeek = [];
    let weekStats = { pnl: 0, trades: 0, wins: 0, journaled: 0, totalTrades: 0 };

    // Add empty cells for days before the 1st
    for (let i = 0; i < firstDay; i++) {
      currentWeek.push({ empty: true, key: `empty-${i}` });
    }

    // Add all days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
      const dayStats = getDailyStats(date);
      const isToday = date.toDateString() === today.toDateString();

      // Accumulate week stats
      if (dayStats) {
        weekStats.pnl += dayStats.pnl;
        weekStats.trades += dayStats.trades;
        weekStats.wins += Math.round(dayStats.trades * dayStats.winRate / 100);
        weekStats.journaled += dayStats.journaled;
        weekStats.totalTrades += dayStats.trades;
      }

      currentWeek.push({
        day,
        date,
        dayStats,
        isToday,
        key: `day-${day}`
      });

      // If we've completed a week (Saturday) or it's the last day
      if (currentWeek.length === 7 || day === daysInMonth) {
        // Pad incomplete weeks with empty cells
        while (currentWeek.length < 7) {
          currentWeek.push({ empty: true, key: `empty-end-${currentWeek.length}` });
        }

        weeks.push({
          days: currentWeek,
          stats: { ...weekStats }
        });

        // Reset for next week
        currentWeek = [];
        weekStats = { pnl: 0, trades: 0, wins: 0, journaled: 0, totalTrades: 0 };
      }
    }

    // Render all weeks
    return weeks.map((week, weekIndex) => (
      <React.Fragment key={`week-${weekIndex}`}>
        {/* Render 7 day cells */}
        {week.days.map((dayData) => {
          if (dayData.empty) {
            return <div key={dayData.key} className="min-h-40"></div>;
          }

          const { day, dayStats, isToday } = dayData;

          // Get journal entry for this day to show event tags. dayData.date was
          // built via `new Date(year, month, day)` (local midnight), so reading
          // local fields round-trips back to the cell's intended YYYY-MM-DD.
          // toISOString() would shift to UTC and mismatch trade.date keys for
          // positive-UTC hosts.
          const d = dayData.date;
          const dateStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
          const journalEntry = journalEntries[dateStr] || {};
          const dayEventTags = journalEntry.eventTags || [];

          // Determine background color based on P&L
          let bgClass = '';
          if (dayStats) {
            if (dayStats.pnl >= 0) {
              bgClass = darkMode ? 'bg-green-900/20' : 'bg-green-50';
            } else {
              bgClass = darkMode ? 'bg-red-900/20' : 'bg-red-50';
            }
          }

          // Override for today
          if (isToday) {
            bgClass = darkMode ? 'bg-blue-900/50' : 'bg-blue-50';
          }

          return (
            <div
              key={dayData.key}
              onClick={() => onNavigateToJournal(dateStr)}
              className={`min-h-40 border rounded-lg p-2 text-sm transition-colors duration-300 flex flex-col cursor-pointer text-center ${bgClass} ${
                isToday
                  ? 'border-blue-400'
                  : darkMode
                    ? 'border-gray-600 hover:border-gray-500'
                    : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className={`font-medium transition-colors duration-300 ${
                isToday
                  ? 'text-blue-600'
                  : darkMode
                    ? 'text-gray-200'
                    : 'text-gray-700'
              }`}>
                {day}
              </div>
              {dayStats && (
                <>
                  <div className="mt-1">
                    <div className={`font-semibold ${dayStats.pnl >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {dayStats.pnl >= 0 ? '' : '-'}${Math.abs(dayStats.pnl).toLocaleString('en-US', { maximumFractionDigits: 0 })}
                    </div>
                  </div>
                  {/* Spacer pushes bottom section down */}
                  <div className="flex-1" />
                  {/* Bottom section - all grouped together */}
                  <div>
                    <div className={`text-xs transition-colors duration-300 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      {dayStats.trades} trade{dayStats.trades !== 1 ? 's' : ''}
                    </div>
                    <div className={`text-xs transition-colors duration-300 mt-1 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      {dayStats.winRate.toFixed(0)}% WR{dayStats.avgR !== 0 && <span className="ml-1">| {dayStats.avgR.toFixed(1)}R</span>}
                    </div>
                    {/* Session pills — color-coded session names */}
                    {(() => {
                      const daySessions = sessionsByDate.get(dateStr) || [];
                      if (daySessions.length === 0) return null;
                      return (
                        <div className="flex flex-wrap gap-1 justify-center mt-2">
                          {daySessions.slice(0, 3).map((s) => {
                            const def = getSessionColorDef(s.color);
                            return (
                              <span
                                key={s.id}
                                className={`px-1.5 py-0.5 rounded text-[10px] whitespace-nowrap font-medium border ${def.bg} ${def.text} ${darkMode ? 'border-white/10' : 'border-gray-200/80'}`}
                                style={{ maxWidth: '5.5rem', overflow: 'hidden', textOverflow: 'ellipsis' }}
                                title={`${s.name} · ${s.count} trade${s.count !== 1 ? 's' : ''} · ${s.pnl >= 0 ? '+' : '-'}$${Math.abs(s.pnl).toFixed(0)}`}
                              >
                                {s.name}
                              </span>
                            );
                          })}
                          {daySessions.length > 3 && (
                            <span className={`text-[10px] ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              +{daySessions.length - 3}
                            </span>
                          )}
                        </div>
                      );
                    })()}
                    {/* Event Tags - Show first 2 when enabled */}
                    {economicCalendarSettings.showOnCalendar && dayEventTags.length > 0 && (
                      <div className="flex flex-wrap gap-1 justify-center mt-2">
                        {dayEventTags.slice(0, 2).map((tag, idx) => (
                          <span
                            key={idx}
                            className={`px-1.5 py-0.5 rounded text-xs whitespace-nowrap ${
                              darkMode ? 'bg-purple-900/50 text-purple-300' : 'bg-purple-100 text-purple-700'
                            }`}
                            style={{ maxWidth: '5.5rem', overflow: 'hidden', textOverflow: 'ellipsis' }}
                          >
                            {tag}
                          </span>
                        ))}
                        {dayEventTags.length > 2 && (
                          <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            +{dayEventTags.length - 2}
                          </span>
                        )}
                      </div>
                    )}
                    {/* Journaling Status */}
                    <div className={`mt-1.5 pt-1 border-t text-xs ${darkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                      {dayStats.allJournaled ? (
                        <span className="text-green-500 font-medium">&#10003; Journaling Complete!</span>
                      ) : (
                        <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>
                          {dayStats.journaled}/{dayStats.trades} Journaled
                        </span>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          );
        })}

        {/* Weekly Total Cell */}
        <div
          className={`min-h-40 border rounded-lg p-2 text-sm transition-colors duration-300 text-center ${
            darkMode
              ? 'bg-black/45 backdrop-blur-md border border-white/10'
              : 'bg-white/30 backdrop-blur-md border border-white/30'
          }`}
        >
          <div className={`font-medium text-xs mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            Week Total
          </div>
          {week.stats.trades > 0 ? (
            <div className="space-y-0.5">
              <div className={`font-bold text-lg ${week.stats.pnl >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                {week.stats.pnl >= 0 ? '+' : '-'}${Math.abs(week.stats.pnl).toLocaleString('en-US', { maximumFractionDigits: 0 })}
              </div>
              <div className={`text-xs ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                {week.stats.trades} trade{week.stats.trades !== 1 ? 's' : ''}
              </div>
              <div className={`text-xs ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                {week.stats.trades > 0 ? ((week.stats.wins / week.stats.trades) * 100).toFixed(0) : 0}% WR
              </div>
              {/* Weekly Journaling Status */}
              <div className={`text-xs mt-1 pt-1 border-t ${darkMode ? 'border-gray-600' : 'border-gray-300'}`}>
                {week.stats.journaled === week.stats.totalTrades ? (
                  <span className="text-green-500">&#10003; All Journaled</span>
                ) : (
                  <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>
                    {week.stats.journaled}/{week.stats.totalTrades}
                  </span>
                )}
              </div>
            </div>
          ) : (
            <div className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              No trades
            </div>
          )}
        </div>
      </React.Fragment>
    ));
  };

  return (
    <>
      <div className={`rounded-lg shadow-md p-4 mb-6 transition-colors duration-300 ${darkMode ? 'bg-black/45 backdrop-blur-md border border-white/10' : 'bg-white/30 backdrop-blur-md border border-white/30'}`}>
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))}
              className={`p-2 rounded-lg transition-colors duration-300 ${
                darkMode ? 'hover:bg-gray-700 text-white' : 'hover:bg-gray-100 text-gray-700'
              }`}
            >
              <ChevronLeft size={20} />
            </button>
            <h2 className={`text-xl font-bold transition-colors duration-300 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
            </h2>
            <button
              onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))}
              className={`p-2 rounded-lg transition-colors duration-300 ${
                darkMode ? 'hover:bg-gray-700 text-white' : 'hover:bg-gray-100 text-gray-700'
              }`}
            >
              <ChevronRight size={20} />
            </button>
          </div>
          <button
            onClick={() => setCurrentMonth(new Date())}
            className={`px-4 py-2 rounded-lg border transition-[background-color,border-color,box-shadow,color] duration-150 ${
              darkMode
                ? 'bg-blue-500/25 border-blue-400/50 text-blue-50 ring-1 ring-inset ring-blue-400/25 hover:bg-blue-500/35 hover:border-blue-400/70 hover:shadow-[0_0_16px_rgba(59,130,246,0.25)]'
                : 'bg-blue-100 border-blue-300 text-blue-800 hover:bg-blue-200 hover:border-blue-400'
            }`}
          >
            Today
          </button>
        </div>
      </div>

      <div className={`rounded-2xl shadow-lg p-6 mb-6 transition-colors duration-300 ${darkMode ? 'bg-black/45 backdrop-blur-md border border-white/10' : 'bg-white/30 backdrop-blur-md border border-white/30'}`}>
        <h3 className={`text-lg font-bold mb-4 transition-colors duration-300 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Monthly Overview</h3>
        <div className="grid grid-cols-5 gap-4">
          {(() => {
            const monthStats = getMonthlyOverview();
            return (
              <>
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600">{monthStats.totalTrades}</div>
                  <div className={`text-sm transition-colors duration-300 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Total Trades</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-orange-600">{monthStats.tradingDays}</div>
                  <div className={`text-sm transition-colors duration-300 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Trading Days</div>
                </div>
                <div className="text-center">
                  <div className={`text-2xl font-bold ${monthStats.totalPnL >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {monthStats.totalPnL >= 0 ? '' : '-'}${Math.abs(monthStats.totalPnL).toLocaleString('en-US', { maximumFractionDigits: 0 })}
                  </div>
                  <div className={`text-sm transition-colors duration-300 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Total P&L</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-purple-600">{monthStats.winRate.toFixed(1)}%</div>
                  <div className={`text-sm transition-colors duration-300 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Win Rate</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-indigo-600">{monthStats.avgR.toFixed(2)}R</div>
                  <div className={`text-sm transition-colors duration-300 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Avg R Multiple</div>
                </div>
              </>
            );
          })()}
        </div>
      </div>

      <div className={`rounded-lg shadow-md p-6 transition-colors duration-300 ${darkMode ? 'bg-black/45 backdrop-blur-md border border-white/10' : 'bg-white/30 backdrop-blur-md border border-white/30'}`}>
        <div className="grid grid-cols-8 gap-2 mb-4">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Week'].map(day => (
            <div key={day} className={`text-center font-medium py-2 transition-colors duration-300 ${
              day === 'Week'
                ? darkMode ? 'text-purple-400' : 'text-purple-600'
                : darkMode ? 'text-gray-300' : 'text-gray-700'
            }`}>
              {day}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-8 gap-2">
          {renderCalendar()}
        </div>
      </div>
    </>
  );
};

export default CalendarTab;
